import React, { useCallback, useEffect, useState } from "react";

interface ExperimentVariantSummary {
  code: string;
  name: string;
  weight: number;
  isControl: boolean;
  stageCount: number;
}

interface ExperimentSummary {
  experimentId: string;
  code: string;
  name: string;
  status: "BORRADOR" | "ACTIVO" | "PAUSADO" | "FINALIZADO";
  enrolledForms: number;
  variants: ExperimentVariantSummary[];
}

interface Enrollment {
  experimentCode: string;
  experimentName: string;
  experimentStatus: string;
  enrolledAt: string;
}

interface ExperimentoABPanelProps {
  contextFormId: string;
  // Modo dividido seleccionado en el modal (puede no estar guardado aún).
  isDividido: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  BORRADOR: "Borrador",
  ACTIVO: "Activo",
  PAUSADO: "Pausado",
  FINALIZADO: "Finalizado",
};

// Transiciones que ofrece el panel (el backend valida las mismas).
const STATUS_ACTIONS: Record<ExperimentSummary["status"], Array<{ status: string; label: string; color: string }>> = {
  BORRADOR: [{ status: "ACTIVO", label: "Activar", color: "#166534" }],
  ACTIVO: [
    { status: "PAUSADO", label: "Pausar", color: "#92400e" },
    { status: "FINALIZADO", label: "Finalizar", color: "#b91c1c" },
  ],
  PAUSADO: [
    { status: "ACTIVO", label: "Reanudar", color: "#166534" },
    { status: "FINALIZADO", label: "Finalizar", color: "#b91c1c" },
  ],
  FINALIZADO: [],
};

const labelStyle: React.CSSProperties = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  color: "#334155",
  marginBottom: "8px",
};

/**
 * "Check A/B" del formulario: inscribe el formulario en un experimento del
 * formulario de postulación (completo vs por etapas). Solo formularios en modo
 * dividido (guardado) pueden participar; un formulario participa en a lo más un
 * experimento activo. Cada cambio queda en la bitácora del formulario.
 */
const ExperimentoABPanel = ({ contextFormId, isDividido }: ExperimentoABPanelProps) => {
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  const userId = sessionStorage.getItem("usuarioId") ?? "";
  const [experiments, setExperiments] = useState<ExperimentSummary[]>([]);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [savedAsSplit, setSavedAsSplit] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!baseUrl || !contextFormId) return;
    setLoading(true);
    setError("");
    try {
      const [experimentsRes, enrollmentRes] = await Promise.all([
        fetch(`${baseUrl}/experiment/active`),
        fetch(`${baseUrl}/experiment/form-enrollment/${encodeURIComponent(contextFormId)}`),
      ]);
      const experimentsBody = experimentsRes.ok ? await experimentsRes.json() : { experiments: [] };
      const enrollmentBody = enrollmentRes.ok ? await enrollmentRes.json() : { enrollment: null, eligible: false };
      const list: ExperimentSummary[] = experimentsBody.experiments ?? [];
      setExperiments(list);
      setEnrollment(enrollmentBody.enrollment ?? null);
      setSavedAsSplit(Boolean(enrollmentBody.eligible));
      setSelectedCode(enrollmentBody.enrollment?.experimentCode ?? list.find((item) => item.status === "ACTIVO")?.code ?? list[0]?.code ?? "");
    } catch {
      setError("No se pudo cargar el estado del experimento A/B.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, contextFormId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (active: boolean) => {
    if (!baseUrl) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${baseUrl}/experiment/form-enrollment?userId=${encodeURIComponent(userId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contextFormId, experimentCode: selectedCode, active, userId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.message ?? "No se pudo actualizar el experimento.");
        return;
      }
      setEnrollment(body.enrollment ?? null);
    } catch {
      setError("No se pudo actualizar el experimento.");
    } finally {
      setSaving(false);
    }
  };

  const selected = experiments.find((item) => item.code === selectedCode);
  const canEnroll = savedAsSplit && isDividido && !!selected;
  // Experimento sobre el que actúan los botones de estado: el inscrito o el seleccionado.
  const target = experiments.find((item) => item.code === (enrollment?.experimentCode ?? selectedCode));

  const changeStatus = async (status: string, label: string) => {
    if (!baseUrl || !target) return;
    const confirmed = window.confirm(
      `${label} ${target.code}?\n\nEl estado es global: afecta a los ${target.enrolledForms} formulario(s) inscritos, no solo a este.`,
    );
    if (!confirmed) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${baseUrl}/experiment/status?userId=${encodeURIComponent(userId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: target.code, status, userId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.message ?? "No se pudo cambiar el estado del experimento.");
        return;
      }
      await load();
    } catch {
      setError("No se pudo cambiar el estado del experimento.");
    } finally {
      setSaving(false);
    }
  };

  // En modo completo el experimento no aplica; solo se muestra si el formulario ya
  // está inscrito, para poder retirarlo.
  if (!isDividido && !enrollment) return null;

  return (
    <div
      data-testid="experimento-ab-panel"
      style={{ marginBottom: "22px", padding: "14px 16px", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#f8fafc" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <div>
          <div style={labelStyle}>Experimento A/B</div>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "12px", color: "#64748b" }}>
            Compara el formulario completo con la postulación por etapas (WhatsApp para completar el paso 2).
          </div>
        </div>
        {enrollment && (
          <span style={{ padding: "3px 10px", borderRadius: "999px", background: "#dcfce7", color: "#166534", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>
            A/B · {enrollment.experimentCode}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ marginTop: "10px", fontSize: "12px", color: "#64748b" }}>Cargando…</div>
      ) : (
        <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {!enrollment && (
            <select
              aria-label="Experimento"
              value={selectedCode}
              onChange={(event) => setSelectedCode(event.target.value)}
              disabled={experiments.length === 0 || saving}
              style={{ padding: "8px 10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", fontFamily: "Roboto, sans-serif" }}
            >
              {experiments.length === 0 && <option value="">No hay experimentos disponibles</option>}
              {experiments.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name} ({item.code}) · {STATUS_LABEL[item.status] ?? item.status}
                </option>
              ))}
            </select>
          )}

          {selected && (
            <div style={{ fontSize: "12px", color: "#475569", fontFamily: "Roboto, sans-serif" }}>
              Variantes: {selected.variants.map((variant) => `${variant.name} (${variant.weight}%)`).join(" · ")}
              {selected.status !== "ACTIVO" && " — el experimento aún no está activo; el formulario se comporta como siempre hasta activarlo."}
            </div>
          )}

          {isDividido && !savedAsSplit && (
            <div style={{ fontSize: "12px", color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "8px 10px" }}>
              Guarda el formulario en modo <strong>por pasos</strong> y vuelve a abrirlo para poder inscribirlo.
            </div>
          )}
          {!isDividido && enrollment && (
            <div style={{ fontSize: "12px", color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "8px 10px" }}>
              El formulario está inscrito pero seleccionaste modo <strong>completo</strong>: retíralo del experimento antes de guardar.
            </div>
          )}

          <div style={{ display: "flex", gap: "8px" }}>
            {enrollment ? (
              <button
                type="button"
                onClick={() => void toggle(false)}
                disabled={saving}
                style={{ padding: "8px 14px", borderRadius: "8px", border: "1px solid #fca5a5", background: "#fff", color: "#b91c1c", fontSize: "13px", cursor: "pointer" }}
              >
                {saving ? "Guardando…" : "Retirar del experimento"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void toggle(true)}
                disabled={!canEnroll || saving}
                style={{
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: canEnroll ? "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)" : "#cbd5e1",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: canEnroll ? "pointer" : "not-allowed",
                }}
              >
                {saving ? "Guardando…" : "Participar en el experimento"}
              </button>
            )}
          </div>

          {target && STATUS_ACTIONS[target.status].length > 0 && (
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "10px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", color: "#475569", fontFamily: "Roboto, sans-serif" }}>
                Estado de {target.code}: <strong>{STATUS_LABEL[target.status] ?? target.status}</strong> · {target.enrolledForms} formulario(s) inscrito(s)
              </span>
              {STATUS_ACTIONS[target.status].map((action) => (
                <button
                  key={action.status}
                  type="button"
                  onClick={() => void changeStatus(action.status, action.label)}
                  disabled={saving}
                  style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", background: "#fff", color: action.color, fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {error && <div role="alert" style={{ fontSize: "12px", color: "#b91c1c" }}>{error}</div>}
        </div>
      )}
    </div>
  );
};

export default ExperimentoABPanel;
